import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, X, Plus, Trash2, Send, CheckCircle2, Target, Sparkles, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';
import SwapExerciseModal from '../SwapExerciseModal';

export default function DraftPlanEditor({ fr, draftPlan, setDraftPlan, deploying, auditing, auditResult, onDeploy, onForceDeployAfterAudit, onClearAudit, onUpdateAudit, onCancel, updateExercise, removeExercise, addExercise }) {
  const [swapTarget, setSwapTarget] = useState(null);

  // Flexible exercise name matching (handles AI variations)
  const exerciseMatch = (planName, conflictName) => {
    if (!planName || !conflictName) return false;
    const a = planName.trim().toLowerCase();
    const b = conflictName.trim().toLowerCase();
    if (a === b) return true;
    const coreA = a.split('(')[0].split('—')[0].split(' - ')[0].trim();
    const coreB = b.split('(')[0].split('—')[0].split(' - ')[0].trim();
    if (coreA === coreB) return true;
    if (a.includes(b) || b.includes(a)) return true;
    if (coreA.includes(coreB) || coreB.includes(coreA)) return true;
    return false;
  };

  const handleAutoFix = () => {
    if (!auditResult?.conflicts?.length || !draftPlan) return;
    const updated = JSON.parse(JSON.stringify(draftPlan));
    let fixCount = 0;
    for (const conflict of auditResult.conflicts) {
      const altName = conflict.alternative_name || conflict.alternative;
      if (!altName) continue;
      for (const week of updated.weeks || []) {
        for (const day of week.days || []) {
          for (const ex of day.exercises || []) {
            if (exerciseMatch(ex.name, conflict.exercise)) {
              ex.name = altName;
              if (conflict.alternative_notes) {
                ex.notes = conflict.alternative_notes;
              }
              fixCount++;
            }
          }
        }
      }
    }
    setDraftPlan(updated);
    onClearAudit();
    toast.success(fr ? `${fixCount} exercice(s) remplace(s) automatiquement !` : `${fixCount} exercise(s) auto-replaced!`);
  };

  const handleSingleFix = (conflict) => {
    const altName = conflict.alternative_name || conflict.alternative;
    if (!altName || !draftPlan) return;
    const updated = JSON.parse(JSON.stringify(draftPlan));
    let found = false;
    for (const week of updated.weeks || []) {
      for (const day of week.days || []) {
        for (const ex of day.exercises || []) {
          if (exerciseMatch(ex.name, conflict.exercise)) {
            ex.name = altName;
            if (conflict.alternative_notes) {
              ex.notes = conflict.alternative_notes;
            }
            found = true;
          }
        }
      }
    }
    if (found) {
      setDraftPlan(updated);
      const remaining = auditResult.conflicts.filter(c => c.exercise !== conflict.exercise);
      if (remaining.length === 0) {
        onClearAudit();
      } else if (onUpdateAudit) {
        onUpdateAudit({ ...auditResult, conflicts: remaining });
      }
      toast.success(fr ? `"${conflict.exercise}" remplace par "${altName}"` : `"${conflict.exercise}" replaced with "${altName}"`);
    }
  };

  return (
    <div className="space-y-3" data-testid="self-plan-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <input
            value={draftPlan.plan_name || ''}
            onChange={e => setDraftPlan(prev => ({ ...prev, plan_name: e.target.value }))}
            className="font-heading text-sm font-bold text-foreground bg-transparent border-b border-border focus:border-primary outline-none w-full"
            data-testid="self-plan-name-input"
          />
        </div>
        <div className="flex gap-2 ml-2 shrink-0">
          <Button size="sm" variant="outline" onClick={onCancel} data-testid="self-cancel-plan">
            <X className="w-3 h-3 mr-1" /> {fr ? 'Annuler' : 'Cancel'}
          </Button>
          <Button size="sm" onClick={onDeploy} disabled={deploying || auditing} className="bg-green-600 hover:bg-green-700 text-white" data-testid="self-deploy-plan">
            {auditing ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> {fr ? 'Audit...' : 'Auditing...'}</> :
             deploying ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> {fr ? 'Deploiement...' : 'Deploying...'}</> :
             <><Send className="w-3 h-3 mr-1" /> {fr ? 'Activer' : 'Activate'}</>}
          </Button>
        </div>
      </div>

      {/* AUDIT ALERT PANEL */}
      {auditResult && auditResult.status === 'alert' && auditResult.conflicts?.length > 0 && (
        <div className="rounded-xl border-2 border-red-500/40 bg-red-500/5 p-4 space-y-3" data-testid="self-audit-alert-panel">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center shrink-0">
              <Target className="w-4 h-4 text-red-400" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-heading font-bold text-red-400 uppercase">
                {fr ? 'MODE EXAMEN — Conflits Detectes' : 'EXAM MODE — Conflicts Detected'}
              </h4>
              <p className="text-[10px] text-red-400/70">
                {fr ? `${auditResult.conflicts.length} exercice(s) potentiellement dangereux` : `${auditResult.conflicts.length} potentially dangerous exercise(s)`}
              </p>
            </div>
          </div>

          {/* BOUTON MAITRE */}
          <Button size="sm" onClick={handleAutoFix} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="self-audit-autofix-btn">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {fr ? `Appliquer toutes les alternatives securitaires (${auditResult.conflicts.filter(c => c.alternative_name || c.alternative).length})` : `Apply all safe alternatives (${auditResult.conflicts.filter(c => c.alternative_name || c.alternative).length})`}
          </Button>

          {auditResult.injuries_declared && (
            <div className="text-[10px] text-muted-foreground bg-red-500/5 rounded-lg p-2">
              <span className="font-medium text-red-400/80">{fr ? 'Blessures: ' : 'Injuries: '}</span>
              {auditResult.injuries_declared}
            </div>
          )}

          <div className="space-y-2">
            {auditResult.conflicts.map((conflict, i) => (
              <div key={i} className="bg-card/80 border border-red-500/20 rounded-lg p-3 space-y-1.5" data-testid={`self-audit-conflict-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-red-400">{conflict.exercise}</span>
                  <span className="text-[9px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">{conflict.injury}</span>
                </div>
                <p className="text-[11px] text-muted-foreground">{conflict.risk}</p>
                {(conflict.alternative_name || conflict.alternative) && (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 text-[11px] bg-green-500/5 rounded-lg px-2.5 py-1.5 space-y-0.5">
                      <p className="font-bold text-green-400">{conflict.alternative_name || conflict.alternative}</p>
                      {conflict.alternative_notes && <p className="text-muted-foreground text-[10px]">{conflict.alternative_notes}</p>}
                    </div>
                    <Button size="sm" onClick={() => handleSingleFix(conflict)} className="shrink-0 h-7 bg-green-600 hover:bg-green-700 text-white text-[10px] px-2" data-testid={`self-audit-fix-single-${i}`}>
                      <ArrowRightLeft className="w-3 h-3 mr-1" /> {fr ? 'Remplacer' : 'Replace'}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-2 pt-1 border-t border-red-500/10">
            <Button size="sm" variant="outline" onClick={onClearAudit} className="flex-1 border-primary/30 text-primary hover:bg-primary/10" data-testid="self-audit-fix-btn">
              <ArrowRightLeft className="w-3 h-3 mr-1" /> {fr ? 'Editer manuellement' : 'Edit manually'}
            </Button>
            <Button size="sm" onClick={onForceDeployAfterAudit} disabled={deploying} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="self-audit-force-deploy-btn">
              {deploying ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
              {fr ? 'Ignorer & Deployer' : 'Override & Deploy'}
            </Button>
          </div>
        </div>
      )}

      {/* AUDIT CLEAR BADGE */}
      {auditResult && auditResult.status === 'clear' && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/10 border border-green-500/20" data-testid="self-audit-clear-badge">
          <CheckCircle2 className="w-4 h-4 text-green-400" />
          <span className="text-xs text-green-400 font-medium">{fr ? 'Audit clinique: Plan securitaire.' : 'Clinical audit: Plan is safe.'}</span>
        </div>
      )}

      {(draftPlan.weeks || []).map((week, wi) => (
        <Card key={wi} className="bg-card/60 border-border">
          <CardContent className="p-3 space-y-2">
            <div className="text-xs font-heading flex items-center justify-between">
              <span>{fr ? `Semaine ${week.week || wi + 1}` : `Week ${week.week || wi + 1}`}</span>
            </div>
            {(week.days || []).map((day, di) => (
              <div key={di} className="bg-secondary/30 rounded-lg p-2">
                <div className="flex items-center justify-between mb-1.5">
                  <input
                    value={day.name || `Jour ${day.day || di + 1}`}
                    onChange={e => {
                      setDraftPlan(prev => {
                        const updated = JSON.parse(JSON.stringify(prev));
                        updated.weeks[wi].days[di].name = e.target.value;
                        return updated;
                      });
                    }}
                    className="text-xs font-bold text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary outline-none flex-1"
                  />
                  <Button size="sm" variant="ghost" onClick={() => addExercise(wi, di)} className="h-5 w-5 p-0 text-green-500 hover:text-green-400" data-testid={`self-add-ex-w${wi}d${di}`}>
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
                <div className="space-y-1">
                  {(day.exercises || []).map((ex, ei) => (
                    <div key={ei} className="flex items-center gap-1.5 text-[11px]">
                      <input value={ex.name} onChange={e => updateExercise(wi, di, ei, 'name', e.target.value)} className="flex-1 bg-background/50 border border-border rounded px-2 py-1 text-xs min-w-0" />
                      <input value={ex.sets} onChange={e => updateExercise(wi, di, ei, 'sets', parseInt(e.target.value) || 0)} className="w-9 bg-background/50 border border-border rounded px-1 py-1 text-xs text-center" title={fr ? 'Séries' : 'Sets'} />
                      <span className="text-muted-foreground text-[10px]">x</span>
                      <input value={ex.reps} onChange={e => updateExercise(wi, di, ei, 'reps', e.target.value)} className="w-12 bg-background/50 border border-border rounded px-1 py-1 text-xs text-center" title="Reps" />
                      <input value={ex.weight || ''} onChange={e => updateExercise(wi, di, ei, 'weight', e.target.value)} className="w-12 bg-background/50 border border-border rounded px-1 py-1 text-xs text-center" title={fr ? 'Poids (lbs)' : 'Weight (lbs)'} placeholder="lbs" />
                      <input value={ex.rest_seconds || ''} onChange={e => updateExercise(wi, di, ei, 'rest_seconds', parseInt(e.target.value) || 0)} className="w-10 bg-background/50 border border-border rounded px-1 py-1 text-xs text-center" title={fr ? 'Repos (sec)' : 'Rest (sec)'} placeholder="sec" />
                      <Button size="sm" variant="ghost" onClick={() => setSwapTarget({ weekIdx: wi, dayIdx: di, exIdx: ei, name: ex.name })} className="h-5 w-5 p-0 text-primary/70 hover:text-primary shrink-0" title={fr ? 'Remplacer' : 'Swap'} data-testid={`self-swap-ex-w${wi}d${di}e${ei}`}>
                        <ArrowRightLeft className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => removeExercise(wi, di, ei)} className="h-5 w-5 p-0 text-red-500/70 hover:text-red-400 shrink-0" data-testid={`self-rm-ex-w${wi}d${di}e${ei}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      {/* Swap Exercise Modal */}
      <SwapExerciseModal
        isOpen={!!swapTarget}
        onClose={() => setSwapTarget(null)}
        currentExerciseName={swapTarget?.name}
        language={fr ? 'fr' : 'en'}
        onSelect={(newName) => {
          if (swapTarget) {
            updateExercise(swapTarget.weekIdx, swapTarget.dayIdx, swapTarget.exIdx, 'name', newName);
          }
        }}
      />
    </div>
  );
}
